import { X } from "lucide-react";
import { Dialog as Primitive } from "radix-ui";
import type { ReactNode } from "react";

/**
 * Panel lateral para ver o editar un registro sin perder la tabla de vista. En celular
 * ocupa toda la pantalla.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  ancho = "normal",
  className,
  overlayClassName,
  onCloseAutoFocus,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  onCloseAutoFocus?: (event: Event) => void;
  /**
   * **Lo ancho que necesita lo que va dentro.**
   *
   * `normal` es el panel de siempre: una ficha, un registro, una columna. `ancho` es para lo que de
   * verdad no cabe en una columna — el panel de producción enseña **las cuatro prensas a la vez**, y
   * ese «a la vez» es el punto: en dos columnas hay que desplazarse para comparar, que es justo lo que
   * se viene a hacer.
   *
   * Es una medida más del mismo panel y no un componente nuevo: dos ventanas distintas acaban con dos
   * formas de cerrarse y dos de poner el foco, y la que se use menos se queda vieja.
   */
  ancho?: "normal" | "ancho";
  /** Clase local de la pantalla que abre el panel; no modifica los demás paneles. */
  className?: string;
  /** Clase local para la sombra de este panel. */
  overlayClassName?: string;
  children: ReactNode;
}) {
  return (
    <Primitive.Root open={open} onOpenChange={onOpenChange}>
      <Primitive.Portal>
        <Primitive.Overlay className={"fixed inset-0 z-30 bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" + (overlayClassName ? ` ${overlayClassName}` : "")} />
        <Primitive.Content
          onCloseAutoFocus={onCloseAutoFocus}
          className={
            "fixed inset-y-0 right-0 z-40 flex w-full flex-col bg-surface text-ink shadow-card data-[state=open]:animate-in data-[state=open]:slide-in-from-right " +
            (ancho === "ancho" ? "max-w-6xl" : "max-w-lg") +
            (className ? ` ${className}` : "")
          }
        >
          <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
            <div>
              <Primitive.Title className="text-lg font-bold tracking-tight">{title}</Primitive.Title>
              {description ? (
                <Primitive.Description className="mt-0.5 text-sm text-ink-2">{description}</Primitive.Description>
              ) : (
                <Primitive.Description className="sr-only">{title}</Primitive.Description>
              )}
            </div>
            <Primitive.Close
              className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-surface-2"
              aria-label="Cerrar"
            >
              <X aria-hidden className="size-5" />
            </Primitive.Close>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}
